import React, { useEffect } from 'react';
import { useActionsContext } from '@cotera/client/v0/actions/framework';
import { ResourceNavigatorModel } from './resource-navigator.model';
import { useResourceNavigator } from './use-resource-navigator';

/**
 * Publishes the surface-aware {@link useResourceNavigator} to the actions world
 * for as long as it's mounted, so actions fired from a palette or a slash menu
 * open resources where the user is instead of navigating the app away.
 *
 * Rendered inside the artifact panel's context provider — one mount point covers
 * both the chat panel and the editor-page panel. Renders nothing.
 *
 * The model is resolved inside the effect rather than at render: nothing here
 * needs to re-render when it appears, and `ModelScopeFactory` withholds its
 * children until registration is done, so by the time this mounts the shell's
 * model is already in scope.
 *
 * The effect cleanup is load-bearing: without popping, an unmounted panel's
 * navigator would stay current and later actions would open into a dead panel.
 */
export const ResourceNavigatorBridge: React.FC = () => {
  const context = useActionsContext();
  const navigator = useResourceNavigator();

  useEffect(() => {
    const model = context.getInScopeModelOfType(ResourceNavigatorModel);
    if (model === null) {
      return;
    }
    return model.push(navigator);
  }, [context, navigator]);

  return null;
};
